// MSAL imports
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";

import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { SwitchableFluentThemeProvider } from './theming/SwitchableFluentThemeProvider';


export const msalInstance = new PublicClientApplication(msalConfig);

// Capture meetingId from initial URL so it survives auth redirects and client-side navigation.
// We do this before MSAL does any redirects or the app navigates.
if (typeof window !== 'undefined') {
  try {
    const params = new URLSearchParams(window.location.search);
    const meetingId = params.get('meetingId');
    if (meetingId) {
      sessionStorage.setItem('incomingMeetingId', meetingId);
      // Also persist to localStorage so components that read meetingId from localStorage can find it
      try { localStorage.setItem('meetingId', meetingId); } catch (e) {}
      // Optional: remove the querystring for aesthetics so it won't be sent to auth endpoints
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState(null, '', cleanUrl);
    }
  } catch (e) {
    // Safely ignore any URL parsing issues in unusual environments
    // (this should not happen in standard browsers)
    // console.warn('Failed to capture meetingId', e);
  }
}

// Account selection logic is app dependent. Adjust as needed for different use cases.
const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
}

msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    msalInstance.setActiveAccount(account);

    // Restore meetingId saved before auth and notify the app
    try {
      const meetingId = sessionStorage.getItem('incomingMeetingId');
      if (meetingId) {
        sessionStorage.removeItem('incomingMeetingId');
        // Dispatch a custom event the app can listen for
        window.dispatchEvent(new CustomEvent('incomingMeetingId', { detail: meetingId }));
        // Optional: auto-navigate to a join route instead of dispatching
        // window.location.href = `/join?meetingId=${encodeURIComponent(meetingId)}`;
      }
    } catch (e) {
      // ignore storage/dispatch errors
    }
  }
});

// Handle redirect promise to capture state returned by Azure AD (for meetingId forwarded in state)
msalInstance.handleRedirectPromise().then((result) => {
  try {
    // MSAL may return a state that includes its own prefix or encoding. Examples:
    // - "msal...|{"meetingId":"..."}"
    // - URL-encoded JSON in the fragment state param
    // - base64 JSON in some setups
    // Try to extract the JSON part reliably and recover meetingId.
    let rawState: string | null = null;
    if (result && (result as any).state) {
      rawState = (result as any).state;
    }
    // If we didn't get state from result, try reading it from fragment (fallback)
    if (!rawState && typeof window !== 'undefined') {
      try {
        const hash = window.location.hash || '';
        const params = new URLSearchParams(hash.replace(/^#/, ''));
        const fragState = params.get('state');
        if (fragState) rawState = fragState;
      } catch (e) {
        // ignore
      }
    }

    if (rawState) {
      // decode URI component if necessary
      let decoded = '';
      try { decoded = decodeURIComponent(rawState); } catch (e) { decoded = rawState; }

      // If MSAL concatenated its own state with our state using '|', take the last segment
      if (decoded.indexOf('|') !== -1) {
        decoded = decoded.split('|').pop() || decoded;
      }

      // Try JSON parse directly
      let parsed: any = null;
      try {
        parsed = JSON.parse(decoded);
      } catch (e) {
        // If it's base64, try to decode then parse
        try {
          // atob may fail for URL-safe base64; replace URL-safe chars
          const b64 = decoded.replace(/-/g, '+').replace(/_/g, '/');
          const jsonStr = atob(b64);
          parsed = JSON.parse(jsonStr);
        } catch (err) {
          // Last resort: try to find a meetingId key with regex
          try {
            const m = decoded.match(/"meetingId"\s*:\s*"([^"]+)"/);
            if (m && m[1]) parsed = { meetingId: m[1] };
          } catch (e2) {
            // give up
          }
        }
      }

      const meetingId = parsed && parsed.meetingId ? parsed.meetingId : null;
      if (meetingId) {
        // store and dispatch so app handles it
        try { sessionStorage.setItem('incomingMeetingId', meetingId); } catch (e) {}
        try { localStorage.setItem('meetingId', meetingId); } catch (e) {}
        try { window.dispatchEvent(new CustomEvent('incomingMeetingId', { detail: meetingId })); } catch (e) {}
      }
    }
  } catch (e) {
    // ignore
  }
}).catch(() => {
  // ignore errors here
});

const container = document.getElementById('root');
const root = createRoot(container);

// ReactDOM.render(

//   <SwitchableFluentThemeProvider scopeId="FluentThemeProvider">
//       <div className="wrapper">
//         <App pca={msalInstance} />
//       </div>
//   </SwitchableFluentThemeProvider>,
//   document.getElementById('root')
// );

root.render(   
  <SwitchableFluentThemeProvider scopeId="FluentThemeProvider">
    <div className="wrapper">
      <App pca={msalInstance} />
    </div>
   </SwitchableFluentThemeProvider>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
